import { useEffect, useRef } from "react";

/**
 * AUDIO ENGINE:
 *
 * audioTracks = [
 *   { id, src, startAt, volume, muted }
 * ]
 */
export function useAudioEngine(audioTracks) {
  const audioMapRef = useRef({});  // id → HTMLAudioElement
  const playingRef = useRef(false);

  // Keep track list updated
  useEffect(() => {
    const audioMap = audioMapRef.current;
    const activeIds = new Set();

    audioTracks.forEach((track) => {
      const { id, src, volume = 1, muted = false } = track;
      activeIds.add(id);

      if (!audioMap[id]) {
        const audio = new Audio(src);
        audio.preload = "auto";
        audio.volume = muted ? 0 : volume;
        audioMap[id] = audio;
      } else {
        audioMap[id].volume = muted ? 0 : volume;
      }
    });

    // Remove old tracks
    Object.keys(audioMap).forEach((id) => {
      if (!activeIds.has(id)) {
        audioMap[id].pause();
        delete audioMap[id];
      }
    });
  }, [audioTracks]);

  /** PLAY all tracks */
  const play = async (startTime = 0) => {
    playingRef.current = true;

    const audioMap = audioMapRef.current;

    for (const track of audioTracks) {
      const audio = audioMap[track.id];
      if (!audio) continue;

      const localTime = startTime - (track.startAt || 0);

      if (localTime < 0) {
        audio.pause();
        continue;
      }

      if (localTime > audio.duration) {
        audio.pause();
        continue;
      }

      try {
        audio.currentTime = Math.max(0, localTime);
        await audio.play();
      } catch (err) {
        console.warn("Audio play blocked:", err);
      }
    }
  };

  /** PAUSE all */
  const pause = () => {
    playingRef.current = false;
    Object.values(audioMapRef.current).forEach((audio) => audio.pause());
  };

  /** SEEK all tracks */
  const seek = (t) => {
    const audioMap = audioMapRef.current;

    for (const track of audioTracks) {
      const audio = audioMap[track.id];
      if (!audio) continue;

      const local = t - (track.startAt || 0);

      if (local < 0 || local > audio.duration) {
        audio.pause();
        continue;
      }

      audio.currentTime = local;

      if (playingRef.current && audio.paused) {
        audio.play().catch(() => {});
      }
    }
  };

  /** MASTER CLOCK = first track that is active */
  const getCurrentTime = () => {
    const map = audioMapRef.current;
    const first = audioTracks[0];

    if (!first) return 0;

    const audio = map[first.id];
    if (!audio) return 0;

    return audio.currentTime + (first.startAt || 0);
  };

  const stopAll = () => {
    playingRef.current = false;
    Object.values(audioMapRef.current).forEach((a) => {
      a.pause();
      a.currentTime = 0;
    });
  };

  return {
    play,
    pause,
    seek,
    stopAll,
    getCurrentTime,
  };
}
